// GDPR/DSGVO-conform consent state.
//
// Design:
// - Nothing external loads until the user makes an explicit choice.
// - Necessary storage (auth_token, language, local trips) needs no consent
//   (Art. 6(1)(f) / § 25 Abs. 2 TDDDG).
// - Categories:
//    - necessary  → always on
//    - functional → Google Fonts and other nice-to-have external resources
//                   that leak the visitor IP to a third party.
//    - statistics → Google Analytics
// - Consent is versioned so we can re-prompt if categories or vendors change.

import { useCallback, useEffect, useSyncExternalStore } from "react";

const STORAGE_KEY = "cookie_consent_v1";
const CURRENT_VERSION = 2;

export const CATEGORIES = ["necessary", "functional", "statistics"];

const DEFAULT_CONSENT = {
  version: CURRENT_VERSION,
  timestamp: null,
  necessary: true,
  functional: false,
  statistics: false,
  // "granted" = user made an explicit choice; "unknown" = banner still to show
  status: "unknown",
};

const listeners = new Set();

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONSENT };
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CURRENT_VERSION)
      return { ...DEFAULT_CONSENT };
    return {
      ...DEFAULT_CONSENT,
      ...parsed,
      necessary: true, // always true
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

let currentState = readFromStorage();

function emit() {
  for (const l of listeners) l();
}

function writeConsent(next) {
  currentState = {
    ...currentState,
    ...next,
    necessary: true,
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    status: "granted",
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
  } catch {
    /* storage disabled */
  }
  emit();
}

export const getConsent = () => currentState;

export const acceptAll = () =>
  writeConsent({ functional: true, statistics: true });

export const rejectAll = () =>
  writeConsent({ functional: false, statistics: false });

export const savePreferences = ({ functional = false, statistics = false }) =>
  writeConsent({ functional, statistics });

export const revokeConsent = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  currentState = { ...DEFAULT_CONSENT };
  emit();
};

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export const useConsent = () => {
  const snap = useSyncExternalStore(
    subscribe,
    () => currentState,
    () => currentState,
  );
  const openPreferences = useCallback(() => {
    window.dispatchEvent(new Event("cookie-consent:open"));
  }, []);
  return { consent: snap, openPreferences };
};

// Register an effect that runs whenever a specific category becomes granted.
export const useConsentEffect = (category, fn) => {
  const { consent } = useConsent();
  useEffect(() => {
    if (consent[category] === true) fn(consent);
  }, [consent, category, fn]);
};
